import React, { useEffect, useState } from "react";
import { Sparkles, ArrowUpRight, Tag, X, Check, Loader2, MessageCircle, Mail } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { AgencyLink as Link, useAgencySlug } from "../lib/agency";
import { useSettings } from "../lib/settings";

const GENDERS = [
  { value: "femenino", label: "Femenino" },
  { value: "masculino", label: "Masculino" },
  { value: "no_binario", label: "No binario" },
  { value: "prefiero_no_decir", label: "Prefiero no decir" },
];

function GuestInterestModal({ promo, onClose }) {
  const agencySlug = useAgencySlug();
  const { settings } = useSettings();
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", age: "", gender: "", city: "", state: "",
  });
  const [contactMode, setContactMode] = useState("email"); // email | whatsapp
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const contactValue = contactMode === "email" ? form.email : form.whatsapp;
    if (!form.name || !contactValue || !form.age || !form.gender || !form.city || !form.state) {
      setErr("Completa todos los campos requeridos.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", {
        promotion_id: promo.id,
        promotion_title: promo.title,
        name: form.name,
        email: contactMode === "email" ? form.email : "",
        whatsapp: contactMode === "whatsapp" ? form.whatsapp : "",
        age: parseInt(form.age, 10),
        gender: form.gender,
        city: form.city,
        state: form.state,
        source: "guest",
        agency_slug: agencySlug,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 fade-up overflow-y-auto"
      data-testid="interest-modal"
      onClick={onClose}
    >
      <div
        className="max-w-lg w-full glass rounded-3xl border border-white/10 cyan-glow overflow-hidden my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-[16/7] overflow-hidden">
          <img src={promo.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <button
            onClick={onClose}
            data-testid="close-interest-modal"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/60 border border-white/15 flex items-center justify-center hover:border-[var(--brand-cyan,#FF85AC)]"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          <div className="absolute bottom-4 left-5">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-1">{promo.destination}</p>
            <h3 className="font-display text-2xl text-white">{promo.title}</h3>
          </div>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="interest-success">
            <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 items-center justify-center mb-5">
              <Check className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" />
            </div>
            <h2 className="font-display text-2xl mb-2">¡Gracias por tu interés!</h2>
            <p className="text-neutral-400 text-sm mb-6">
              Un agente de {settings.agency_name} te contactará en menos de 24 horas por {contactMode === "email" ? "email" : "WhatsApp"}.
            </p>
            <button
              onClick={onClose}
              className="bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow"
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 space-y-5">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)]">Solicita información</p>

            {/* Contact toggle */}
            <div className="flex gap-2">
              {[
                { id: "email", label: "Email", icon: Mail },
                { id: "whatsapp", label: "WhatsApp", icon: MessageCircle },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setContactMode(c.id)}
                  data-testid={`contact-mode-${c.id}`}
                  className={`flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-xs uppercase tracking-[0.2em] border transition-all ${
                    contactMode === c.id
                      ? "bg-[var(--brand-cyan,#FF85AC)] text-black border-[var(--brand-cyan,#FF85AC)]"
                      : "border-white/15 text-neutral-400 hover:border-[var(--brand-cyan,#FF85AC)]/50"
                  }`}
                >
                  <c.icon className="w-3.5 h-3.5" /> {c.label}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Nombre</label>
                <input
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  data-testid="lead-name-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-sm text-white"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">
                  {contactMode === "email" ? "Email" : "WhatsApp (con LADA)"}
                </label>
                <input
                  type={contactMode === "email" ? "email" : "tel"}
                  value={contactMode === "email" ? form.email : form.whatsapp}
                  onChange={(e) => update(contactMode === "email" ? "email" : "whatsapp", e.target.value)}
                  placeholder={contactMode === "email" ? "tu@email.com" : "+52 55 1234 5678"}
                  data-testid="lead-contact-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Edad</label>
                <input
                  type="number"
                  min="13"
                  max="120"
                  value={form.age}
                  onChange={(e) => update("age", e.target.value)}
                  data-testid="lead-age-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Género</label>
                <select
                  value={form.gender}
                  onChange={(e) => update("gender", e.target.value)}
                  data-testid="lead-gender-select"
                  className="w-full bg-black border border-white/15 rounded-lg px-3 py-2 text-sm focus:border-[var(--brand-cyan,#FF85AC)] outline-none"
                >
                  <option value="">Selecciona…</option>
                  {GENDERS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Ciudad</label>
                <input
                  value={form.city}
                  onChange={(e) => update("city", e.target.value)}
                  data-testid="lead-city-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-sm text-white"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Estado</label>
                <input
                  value={form.state}
                  onChange={(e) => update("state", e.target.value)}
                  data-testid="lead-state-input"
                  className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-sm text-white"
                />
              </div>
            </div>

            {err && <p className="text-sm text-[#FF3B30]" data-testid="lead-error">{err}</p>}

            <button
              type="submit"
              disabled={loading}
              data-testid="submit-lead-btn"
              className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-3.5 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow transition-all disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Enviar solicitud
            </button>

            <p className="text-[10px] text-neutral-600 text-center">
              Al enviar aceptas recibir comunicaciones de {settings.agency_name} y sus partners.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function TravelerNotifyModal({ promo, user, onClose }) {
  const { settings } = useSettings();
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const send = async () => {
    setLoading(true);
    setErr("");
    try {
      await api.post("/leads", {
        promotion_id: promo.id,
        promotion_title: promo.title,
        name: user.traveler_name,
        email: "",
        whatsapp: "",
        city: "",
        state: "",
        source: "traveler",
        booking_id: user.booking_id,
      });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 fade-up"
      data-testid="traveler-notify-modal"
      onClick={onClose}
    >
      <div
        className="max-w-md w-full glass rounded-3xl border border-white/10 cyan-glow p-8 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-cyan,#FF85AC)]/10 border border-[var(--brand-cyan,#FF85AC)]/40 items-center justify-center mb-5">
          {sent ? <Check className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" /> : <Sparkles className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" />}
        </div>
        <h2 className="font-display text-2xl mb-2">
          {sent ? "Aviso enviado" : "Notificar a tu agente"}
        </h2>
        <p className="text-sm text-neutral-400 mb-6">
          {sent
            ? `Tu agente de ${settings.agency_name} recibirá una alerta sobre tu interés en "${promo.title}" y te contactará pronto.`
            : `Como ya eres cliente de ${settings.agency_name}, sólo notificaremos a tu agente sobre tu interés en "${promo.title}". No necesitas llenar formulario.`}
        </p>
        {err && <p className="text-sm text-[#FF3B30] mb-3" data-testid="notify-error">{err}</p>}
        {sent ? (
          <button
            onClick={onClose}
            className="bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow"
          >
            Cerrar
          </button>
        ) : (
          <div className="flex gap-3 justify-center">
            <button
              onClick={send}
              disabled={loading}
              data-testid="confirm-notify-btn"
              className="bg-[var(--brand-cyan,#FF85AC)] text-black px-7 py-3 rounded-full font-semibold hover:bg-[#FF9DC0] cyan-glow disabled:opacity-50 inline-flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Notificar a mi agente
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-full border border-white/15 hover:border-white/30 text-sm"
            >
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Promociones() {
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const user = auth.current();
  const isTraveler = user && user.role === "traveler";
  const agencySlug = useAgencySlug();
  const { settings } = useSettings();

  useEffect(() => {
    api.get("/promotions", { params: { agency_slug: agencySlug || undefined } }).then(({ data }) => {
      setPromos(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [agencySlug]);

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6 fade-up">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-4">
              {isTraveler ? "Para ti, viajero" : "Modo Invitado"}
            </p>
            <h1 className="font-display text-5xl md:text-6xl font-light tracking-tight max-w-3xl">
              Promociones <span className="text-[var(--brand-cyan,#FF85AC)]">exclusivas</span>
            </h1>
            <p className="text-neutral-400 mt-4 max-w-xl">
              {isTraveler
                ? `Como cliente de ${settings.agency_name}, sólo notifica a tu agente con un toque.`
                : "Descubre los destinos premium de la temporada. Sin login, sin compromisos."}
            </p>
          </div>
          {!isTraveler && (
            <Link
              to="/groups"
              data-testid="create-group-cta"
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border border-[var(--brand-cyan,#FF85AC)]/40 hover:bg-[var(--brand-cyan,#FF85AC)]/10 text-[var(--brand-cyan,#FF85AC)] transition-all"
            >
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm uppercase tracking-[0.2em]">Crear Grupo SOS</span>
            </Link>
          )}
        </div>

        {loading ? (
          <p className="text-neutral-500">Cargando promociones…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {promos.map((p, i) => (
              <article
                key={p.id}
                data-testid={`promo-card-${i}`}
                className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A] hover:border-[var(--brand-cyan,#FF85AC)]/40 transition-all fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img
                    src={p.image_url}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                  <div className="absolute top-5 left-5 flex items-center gap-2">
                    <span className="px-3 py-1 text-[10px] uppercase tracking-[0.25em] bg-black/60 border border-[var(--brand-cyan,#FF85AC)]/40 text-[var(--brand-cyan,#FF85AC)] rounded-full">
                      {p.tag}
                    </span>
                    {p.discount > 0 && (
                      <span className="px-3 py-1 text-[10px] uppercase tracking-[0.25em] bg-[var(--brand-cyan,#FF85AC)] text-black rounded-full font-bold">
                        −{p.discount}%
                      </span>
                    )}
                  </div>

                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--brand-cyan,#FF85AC)] mb-2">
                      <Tag className="inline w-3 h-3 mr-1 -mt-0.5" />
                      {p.destination}
                    </p>
                    <h3 className="font-display text-2xl md:text-[1.7rem] font-light leading-tight mb-2 text-white">
                      {p.title}
                    </h3>
                    <p className="text-sm text-neutral-300 mb-4 line-clamp-2">{p.subtitle}</p>
                    <div className="flex items-end justify-between gap-3 mb-4">
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">Desde</p>
                        <p className="font-display text-2xl text-white">
                          ${p.price_from.toLocaleString()} <span className="text-xs text-neutral-400">{p.currency}</span>
                        </p>
                      </div>
                      <ArrowUpRight className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)] group-hover:scale-125 transition-transform" />
                    </div>
                    <button
                      onClick={() => setSelected(p)}
                      data-testid={`interest-btn-${i}`}
                      className="w-full bg-[var(--brand-cyan,#FF85AC)] text-black py-2.5 rounded-full text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#FF9DC0] cyan-glow transition-all"
                    >
                      {isTraveler ? "Notificar a mi agente" : "Me interesa"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selected && (
        isTraveler
          ? <TravelerNotifyModal promo={selected} user={user} onClose={() => setSelected(null)} />
          : <GuestInterestModal promo={selected} onClose={() => setSelected(null)} />
      )}
    </Layout>
  );
}
